// Which actions need signing off, and by whom.
//
// Every rule ships disabled. The gate they control sits in front of the till —
// a rule that switched itself on would stop a sale with a customer standing
// there — so turning one on is a decision made here, deliberately, by someone
// who can see what it will do.

import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { useApprovalRules, useSetApprovalRule } from '@/hooks/useApprovals';
import { useRoles } from '@/hooks/useRoles';
import { usePermissions } from '@/hooks/usePermissions';
import { formatIDR } from '@/lib/format';

export function ApprovalRulesForm() {
  const { can } = usePermissions();
  const editable = can('settings', 'edit');
  const { data: rules, isLoading } = useApprovalRules();
  const { data: roles } = useRoles();
  const save = useSetApprovalRule();
  const [error, setError] = useState<string | null>(null);

  // A rule needing L2 with nobody at L2 or above is a rule that blocks the
  // action outright and never clears. Worth saying before it happens rather
  // than after someone cannot sell anything.
  const levelsCovered = new Set(
    (roles ?? [])
      .filter((r) => r.approvalLevel !== null && r.userCount > 0)
      .map((r) => r.approvalLevel as number),
  );
  const highestApprover = Math.max(0, ...levelsCovered);

  const patch = async (kind: string, p: Parameters<typeof save.mutateAsync>[0]['patch']) => {
    setError(null);
    try {
      await save.mutateAsync({ kind, patch: p });
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (isLoading) return <LoadingState message="Memuat aturan…" />;

  return (
    <div className="space-y-4">
      <p className="text-small text-ink-subtle leading-relaxed">
        Saat aktif, aksi di bawah ini tidak bisa dijalankan sampai ada persetujuan.
        Pemegang role dengan hak terkait (mis. “Jual tempo”) tidak ikut terhalang —
        merekalah yang dimintai persetujuan.
      </p>

      {error && (
        <div className="p-3 rounded border border-danger/30 bg-danger/5 text-caption text-danger">
          {error}
        </div>
      )}

      {(rules ?? []).map((rule) => {
        const unreachable = rule.enabled && rule.requiredLevel > highestApprover;
        return (
          <Card key={rule.kind} padding="md">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <label className="inline-flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={rule.enabled}
                    disabled={!editable || save.isPending}
                    onChange={(e) => patch(rule.kind, { enabled: e.target.checked })}
                  />
                  <span>
                    <span className="text-body font-medium text-ink">{rule.label}</span>
                    <span className="block text-caption text-ink-subtle mt-0.5">
                      {rule.description}
                    </span>
                  </span>
                </label>

                {unreachable && (
                  <p className="mt-2 text-caption text-danger">
                    Tidak ada user aktif dengan level {rule.requiredLevel} atau lebih tinggi.
                    Permintaan akan menumpuk tanpa bisa disetujui.
                  </p>
                )}
              </div>

              <div className="flex items-end gap-3">
                <Select
                  label="Level"
                  value={String(rule.requiredLevel)}
                  disabled={!editable || save.isPending}
                  onChange={(v) => patch(rule.kind, { requiredLevel: Number(v) })}
                  className="w-36"
                >
                  <option value="1">L1</option>
                  <option value="2">L2</option>
                  <option value="3">L3 (final)</option>
                </Select>

                {rule.threshold !== null && (
                  <Input
                    label="Batas (Rp)"
                    type="number"
                    min={0}
                    defaultValue={rule.threshold}
                    disabled={!editable || save.isPending}
                    className="w-40 tabular-nums"
                    hint={formatIDR(rule.threshold)}
                    onBlur={(e) => {
                      const next = Number(e.target.value);
                      if (Number.isFinite(next) && next !== rule.threshold) {
                        void patch(rule.kind, { threshold: next });
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
